import { useReducer, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Collapse, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { authHeaders, getBackendBaseUrl } from "../../../services/nodeAuthService";
import LogoutPage from "./Logout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function getLoggedInUser() {
    try {
        const parsed = JSON.parse(localStorage.getItem("userDetails") || "{}");
        const name = parsed?.userName || parsed?.name || "Admin";
        const imageUrl = parsed?.imageUrl || "";
        return { name, imageUrl };
    } catch {
        return { name: "Admin", imageUrl: "" };
    }
}

function getInitials(name) {
    return name
        .split(" ")
        .filter(Boolean)
        .map((w) => w[0].toUpperCase())
        .slice(0, 2)
        .join("");
}

/* ── reducer ─────────────────────────────────────────────────── */
const reducer = (prev, updated) => ({ ...prev, ...updated });

/* ── helpers ─────────────────────────────────────────────────── */

function iconClass(icon) {
    if (!icon) return "fa fa-circle";
    // Already a full multi-class string e.g. "fa fa-cogs" or "flaticon-..."
    if (icon.includes(" ") || icon.startsWith("flaticon")) return icon;
    // Bare icon name with "fa-" prefix e.g. "fa-cogs" → "fa fa-cogs"
    if (icon.startsWith("fa-")) return `fa ${icon}`;
    // Plain icon name e.g. "cogs" → "fa fa-cogs"
    return `fa fa-${icon}`;
}

/* ── component ───────────────────────────────────────────────── */
const SideBar = () => {
    const [state, setState] = useReducer(reducer, { active: "", activeSubmenu: "" });
    const [visibleMenu, setVisibleMenu] = useState([]);

    const { name: loggedInName, imageUrl } = getLoggedInUser();
    const resolvedImage = imageUrl
        ? (imageUrl.startsWith("http") ? imageUrl : `${API_BASE_URL}/${imageUrl.replace(/^\//, "")}`)
        : "";

    const [hideOnScroll, setHideOnScroll] = useState(true);
    useScrollPosition(({ prevPos, currPos }) => {
        const isShow = currPos.y > prevPos.y;
        if (isShow !== hideOnScroll) setHideOnScroll(isShow);
    }, [hideOnScroll]);

    const { menus, loading } = useSelector((state) => state.permissions);

    /* ── filter menu whenever permissions change ─────────────────── */
    useEffect(() => {
        if (loading) return;
        setVisibleMenu(Array.isArray(menus) ? menus : []);
    }, [menus, loading]);

    // Fallback: if permission store has no menus, fetch directly to keep sidebar usable.
    useEffect(() => {
        if (loading) return;
        if (Array.isArray(menus) && menus.length > 0) return;

        let isMounted = true;
        fetch(`${getBackendBaseUrl()}/api/admin/menus/my-menus`, { headers: { ...authHeaders() } })
            .then((res) => res.json().catch(() => ({})))
            .then((data) => {
                const fetched = data?.results?.data?.menus;
                if (isMounted && Array.isArray(fetched)) {
                    setVisibleMenu(fetched);
                }
            })
            .catch(() => { });

        return () => {
            isMounted = false;
        };
    }, [menus, loading]);

    /* ── active state from URL ───────────────────────────────────── */
    let path = window.location.pathname;
    path = path.split("/");
    path = path[path.length - 1];

    useEffect(() => {
        visibleMenu.forEach((menu) => {
            const menuPath = String(menu.path || "").replace(/^\/+/, "");
            if (menuPath && menuPath === path) setState({ active: menu.name });
            (menu.children || []).forEach((child) => {
                const childPath = String(child.path || "").replace(/^\/+/, "");
                if (childPath === path) setState({ active: menu.name });
            });
        });
    }, [path, visibleMenu]);

    const handleMenuActive = (status) => setState({ active: state.active === status ? "" : status });
    const handleSubmenuActive = (status) => setState({ activeSubmenu: state.activeSubmenu === status ? "" : status });

    const renderMenuNode = (item, isChild = false) => {
        const key = String(item._id || item.key || item.path || item.name);
        const children = Array.isArray(item.children) ? item.children : [];
        const hasChildren = children.length > 0;
        const itemPath = String(item.path || "").replace(/^\/+/, "");

        return (
            <li key={key} className={state.active === item.name ? "mm-active" : ""}>
                {hasChildren ? (
                    <>
                        <Link to="#" className="has-arrow" onClick={() => handleMenuActive(item.name)}>
                            {!isChild && <i className={iconClass(item.icon)}></i>}
                            <span className="nav-text">{item.name}</span>
                        </Link>
                        <Collapse in={state.active === item.name}>
                            <ul className="mm-show">
                                {children.map((child) => renderMenuNode(child, true))}
                            </ul>
                        </Collapse>
                    </>
                ) : (
                    <Link to={itemPath} className={itemPath === path ? "mm-active" : ""}>
                        {!isChild && <i className={iconClass(item.icon)}></i>}
                        <span className="nav-text">{item.name}</span>
                    </Link>
                )}
            </li>
        );
    };

    /* ── render ──────────────────────────────────────────────────── */
    return (
        <div className="deznav">
            <PerfectScrollbar className="deznav-scroll">
                <ul className="metismenu" id="menu">
                    {visibleMenu.map((item) => renderMenuNode(item))}
                </ul>
                <div className="deznav-profile">
                    <ul className="navbar-nav header-right">
                        <Dropdown as="li"
                            className={`nav-item header-profile`}
                        // onClick={() => onProfile()}
                        >
                            <Dropdown.Toggle to={"#"} as="a"
                                className="nav-link  i-false"
                            >

                                {resolvedImage ? (
                                    <img src={resolvedImage} width="36" height="36" alt={loggedInName}
                                        style={{ borderRadius: "50%", objectFit: "cover" }} />
                                ) : (
                                    <div style={{
                                        width: 36, height: 36, borderRadius: "50%",
                                        background: "#6366f1", color: "#fff",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 13, fontWeight: 700, flexShrink: 0,
                                    }}>
                                        {getInitials(loggedInName)}
                                    </div>
                                )}
                                <div className="header-info">
                                    {/* <small>Good Morning</small> */}
                                    <span>{loggedInName}</span>
                                </div>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="dropdown-menu">
                                <Link
                                    to="/app-profile"
                                    className="dropdown-item ai-icon"
                                >
                                    <svg
                                        id="icon-user1"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="text-primary"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                    </svg>
                                    <span className="ms-2">Profile </span>
                                </Link>

                                <LogoutPage />
                            </Dropdown.Menu>
                        </Dropdown>

                    </ul>
                </div>
            </PerfectScrollbar>

        </div>
    );
};

export default SideBar;
